import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Menu, Edit, MoreVertical, Power, MessageSquare, WifiOff, Zap, Pause, Play, ChevronDown, SlidersHorizontal, Trash2, LogOut } from 'lucide-react';
import { claudeChatAPI } from '../../services/api';
import { useIsAppView, FullSheet, Switch } from '../../components/users/kit';
import { AppSheet } from '../../components/common/AppViewKit';
import useDarkReady from '../../theme/useDarkReady';
import SessionList from './SessionList';
import ChatView from './ChatView';
import { isDisabledError } from './shouldReconnect';
import { sessionLabel } from './chatHelpers';
import './claudeChat.css';

// Height that fits the page between its own top edge and the lowest visible point: the bottom
// of the visual viewport (follows the on-screen keyboard) or the app-mode bottom tab bar,
// whichever is higher. A fixed "screen minus N px" guess pushed the composer under the tab bar.
const BOTTOM_GAP = 12;
function useFitHeight() {
  const node = useRef(null);
  const [h, setH] = useState(360);
  const measure = useCallback(() => {
    const el = node.current;
    if (!el) return;
    const vv = window.visualViewport;
    let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const nav = el.closest('.app-view')?.querySelector(':scope > nav');
    if (nav) bottom = Math.min(bottom, nav.getBoundingClientRect().top);
    setH(Math.max(260, Math.floor(bottom - el.getBoundingClientRect().top - BOTTOM_GAP)));
  }, []);
  const ref = useCallback((el) => { node.current = el; if (el) measure(); }, [measure]);
  useEffect(() => {
    const vv = window.visualViewport;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true); // the app shell scrolls <main>, not the window
    vv?.addEventListener('resize', measure);
    vv?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      vv?.removeEventListener('resize', measure);
      vv?.removeEventListener('scroll', measure);
    };
  }, [measure]);
  return [ref, h];
}
const shortTokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n || 0));
const errMsg =(e) => e?.response?.data?.message || e?.message || 'Something went wrong';

function CenterCard({ icon: Icon, title, text, action, onAction, fit }) {
  return (
    <div ref={fit[0]} className="cc-root cc-center" style={{ height: fit[1] }}>
      <div className="cc-card">
        {Icon && <Icon size={28} className="cc-card-icon" />}
        {title && <h2 className="cc-card-title">{title}</h2>}
        {text && <p className="cc-card-text">{text}</p>}
        {action && <button type="button" className="cc-btn cc-btn-primary" onClick={onAction}>{action}</button>}
      </div>
    </div>
  );
}

export default function ClaudeChatPage() {
  useDarkReady();
  const [phase, setPhase] = useState('loading'); // loading | offline | disabled | open
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [menu, setMenu] = useState(false);
  const [sheet, setSheet] = useState(null); // mobile: 'chats' | 'settings' | null
  const mobile = useIsAppView();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const fit = useFitHeight();

  const refresh = useCallback(async () => {
    try {
      const s = (await claudeChatAPI.status()).data.data;
      if (!s.sidecarUp && s.enabled) setPhase('offline');
      else if (!s.enabled) setPhase('disabled');
      else setPhase('open');
    } catch (e) { toast.error(errMsg(e)); setPhase('offline'); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // A call that comes back 403 CLAUDECHAT_DISABLED (or a stream that ends switched off) lands here.
  const handleDisabled = useCallback(() => setPhase('disabled'), []);

  const loadSessions = useCallback(async () => {
    try {
      const list = (await claudeChatAPI.sessions()).data.data;
      setSessions(list);
      setActiveId((cur) => cur || list[0]?._id || null);
    } catch (e) {
      if (isDisabledError(e)) handleDisabled();
      else toast.error(errMsg(e));
    }
  }, [handleDisabled]);
  useEffect(() => { if (phase === 'open') loadSessions(); }, [phase, loadSessions]);
  // Keeps the list's "Using tokens" badges and counts current.
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const t = setInterval(() => claudeChatAPI.sessions().then((r) => setSessions(r.data.data)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [phase, loadSessions]);

  // Close the overflow menu on any outside tap.
  useEffect(() => {
    if (!menu) return undefined;
    const on = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', on);
    document.addEventListener('touchstart', on);
    return () => { document.removeEventListener('mousedown', on); document.removeEventListener('touchstart', on); };
  }, [menu]);

  const newChat = async () => {
    try {
      const s = (await claudeChatAPI.createSession('')).data.data;
      setSessions((cur) => [s, ...cur]);
      setActiveId(s._id);
      setDrawer(false);
      setSheet(null);
    } catch (e) {
      if (isDisabledError(e)) handleDisabled();
      else toast.error(errMsg(e));
    }
  };

  // Returns true when the chats are gone, so the list can leave select mode.
  const deleteChats = async (ids) => {
    try {
      await claudeChatAPI.deleteSessions(ids);
      const left = sessions.filter((s) => !ids.includes(s._id));
      setSessions(left);
      if (ids.includes(activeId)) setActiveId(left[0]?._id || null);
      toast.success(`Deleted ${ids.length} chat${ids.length > 1 ? 's' : ''}`);
      return true;
    } catch (e) {
      if (isDisabledError(e)) handleDisabled();
      else toast.error(errMsg(e));
      loadSessions(); // some may have been deleted before the failure
      return false;
    }
  };

  const toggleEnabled = async (value) => {
    try { await claudeChatAPI.setEnabled(value); toast.success(value ? 'Claude Code chat switched on' : 'Claude Code chat switched off'); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  // Auto mode for the open chat: approval cards are skipped (policy blocks still apply).
  const toggleAuto = async (session) => {
    const on = !session.autoApprove;
    try {
      const d = (await claudeChatAPI.setAuto(session._id, on)).data.data;
      setSessions((cur) => cur.map((s) => (s._id === session._id ? { ...s, autoApprove: d.autoApprove } : s)));
      toast.success(on ? `Auto mode on${d.approved ? ` (${d.approved} waiting approved)` : ''}` : 'Auto mode off: actions will ask again');
    } catch (e) {
      if (isDisabledError(e)) handleDisabled();
      else toast.error(errMsg(e));
    }
  };

  if (phase === 'loading') return <CenterCard fit={fit} text="Loading…" />;
  if (phase === 'offline') {
    return <CenterCard fit={fit} icon={WifiOff} title="Chat service offline" text="The rest of the CRM is unaffected." action="Retry" onAction={refresh} />;
  }
  if (phase === 'disabled') {
    return <CenterCard fit={fit} icon={Power} title="Claude Code chat is switched off" text="Switch it on to chat with Claude Code from here." action="Switch on" onAction={() => toggleEnabled(true)} />;
  }

  // List play/pause: pause stops the run (the chat stays); play sends one "continue" message.
  const pauseChat = async (id) => {
    try { await claudeChatAPI.stop(id); } catch (e) { toast.error(errMsg(e)); }
    loadSessions();
  };
  const playChat = async (id) => {
    try { await claudeChatAPI.send(id, 'Continue from where you stopped.'); } catch (e) { toast.error(errMsg(e)); }
    loadSessions();
  };
  const active = sessions.find((s) => s._id === activeId);
  // Exit: stop the open session's run (no more tokens) and leave the page. The chat stays in the list.
  const exitSession = async () => {
    if (active?.status === 'running') await claudeChatAPI.stop(active._id).catch(() => {});
    navigate('/dashboard');
  };

  if (mobile) {
    const running = active?.status === 'running';
    const sub = running ? '● Working' : active?.paused ? 'Paused' : active ? 'Idle' : 'No chat open';
    const usage = active?.tokensUsed > 0 ? ` · ${shortTokens(active.tokensUsed)} used` : '';
    return (
      <div ref={fit[0]} className="cc-root cc-page cc-mobile" style={{ height: fit[1] }}>
        <header className="cc-mbar">
          <button type="button" className="cc-mtitle" onClick={() => setSheet('chats')} aria-label="Switch chat">
            <span className="cc-mtitle-text">{active ? sessionLabel(active) : 'Claude Code'}</span>
            <span className={`cc-msub${running ? ' cc-live' : ''}`}>{sub}{usage}</span>
          </button>
          <ChevronDown size={16} className="cc-mchev" aria-hidden="true" />
          <button type="button" className="cc-icon-btn" onClick={newChat} aria-label="New session" title="New session"><Edit size={20} /></button>
          <button type="button" className="cc-icon-btn" onClick={() => setSheet('settings')} aria-label="Chat settings">
            <SlidersHorizontal size={20} />{active?.autoApprove && <span className="cc-mdot" title="Auto mode on" />}
          </button>
        </header>
        <main className="cc-main">
          {activeId ? <ChatView key={activeId} sessionId={activeId} onDisabled={handleDisabled} mobile /> : (
            <div className="cc-center-fill">
              <div className="cc-card">
                <MessageSquare size={28} className="cc-card-icon" />
                <h2 className="cc-card-title">What should we work on?</h2>
                <button type="button" className="cc-btn cc-btn-primary" onClick={newChat}>Start a new session</button>
              </div>
            </div>
          )}
        </main>
        {sheet === 'chats' && (
          <FullSheet title="Chats" onClose={() => setSheet(null)}>
            <div className="cc-root">
              <SessionList sessions={sessions} activeId={activeId} onSelect={(id) => { setActiveId(id); setSheet(null); }} onNew={newChat} onDelete={deleteChats} onPause={pauseChat} onPlay={playChat} />
            </div>
          </FullSheet>
        )}
        {sheet === 'settings' && (
          <AppSheet title="Chat settings" onClose={() => setSheet(null)}>
            <div className="cc-root cc-settings">
              {active && (
                <>
                  <div className="cc-srow">
                    <div className="cc-srow-text">
                      <strong>Auto mode</strong>
                      <span>{active.autoApprove ? 'On: actions run without asking. Blocked commands stay blocked.' : 'Off: each risky action asks for your approval.'}</span>
                    </div>
                    <Switch checked={!!active.autoApprove} onChange={() => toggleAuto(active)} label="Auto mode" />
                  </div>
                  <div className="cc-srow">
                    <div className="cc-srow-text">
                      <strong>Usage</strong>
                      <span>{active.tokensUsed > 0 ? `${shortTokens(active.tokensUsed)} used · ${shortTokens(active.context)} in context` : 'Nothing used yet'}</span>
                    </div>
                  </div>
                  {running && <button type="button" className="cc-btn cc-sbtn" onClick={() => { pauseChat(active._id); setSheet(null); }}><Pause size={16} /> Pause (stop using tokens)</button>}
                  {!running && active.paused && <button type="button" className="cc-btn cc-sbtn" onClick={() => { playChat(active._id); setSheet(null); }}><Play size={16} /> Continue this chat</button>}
                  <button type="button" className="cc-btn cc-sbtn" onClick={() => { setSheet(null); newChat(); }}><Edit size={16} /> New session</button>
                  <button type="button" className="cc-btn cc-sbtn" onClick={() => { setSheet(null); exitSession(); }}><LogOut size={16} /> Exit session</button>
                  <button type="button" className="cc-btn cc-sbtn cc-sdanger" onClick={() => { if (window.confirm('Delete this chat? This cannot be undone.')) { setSheet(null); deleteChats([active._id]); } }}><Trash2 size={16} /> Delete this chat</button>
                </>
              )}
              <button type="button" className="cc-btn cc-sbtn cc-sdanger" onClick={() => { if (window.confirm('Switch Claude Code chat off? Open chats will stop streaming.')) { setSheet(null); toggleEnabled(false); } }}><Power size={16} /> Switch Claude Code chat off</button>
            </div>
          </AppSheet>
        )}
      </div>
    );
  }

  return (
    <div ref={fit[0]} className="cc-root cc-page" style={{ height: fit[1] }}>
      <header className="cc-header">
        <button type="button" className="cc-icon-btn cc-only-narrow" onClick={() => setDrawer(true)} aria-label="Open chats"><Menu size={20} /></button>
        <div className="cc-header-title">
          {active ? sessionLabel(active) : 'Claude Code'}
          {active?.status === 'running' && <span className="cc-head-status cc-live">● Using tokens</span>}
          {active && active.status !== 'running' && active.paused && <span className="cc-head-status">Paused</span>}
        </div>
        {active?.status === 'running' && (
          <button type="button" className="cc-icon-btn cc-pp" onClick={() => pauseChat(active._id)} aria-label="Pause: stop using tokens" title="Pause: stop using tokens"><Pause size={18} /></button>
        )}
        {active && active.status !== 'running' && active.paused && (
          <button type="button" className="cc-icon-btn cc-pp" onClick={() => playChat(active._id)} aria-label="Play: continue this chat" title="Play: continue this chat"><Play size={18} /></button>
        )}
        {active && (
          <button
            type="button" className={`cc-auto${active.autoApprove ? ' cc-auto-on' : ''}`} onClick={() => toggleAuto(active)}
            aria-pressed={!!active.autoApprove} title={active.autoApprove ? 'Auto mode on: actions run without asking' : 'Auto mode off: actions ask for approval'}
          ><Zap size={14} /> Auto<span className="cc-auto-state"> {active.autoApprove ? 'on' : 'off'}</span></button>
        )}
        <button type="button" className="cc-icon-btn" onClick={newChat} aria-label="New session" title="New session"><Edit size={20} /></button>
        <button type="button" className="cc-icon-btn" onClick={exitSession} aria-label="Exit session" title="Exit session (stops the run and leaves)"><LogOut size={20} /></button>
        <div className="cc-menu-wrap" ref={menuRef}>
          <button type="button" className="cc-icon-btn" onClick={() => setMenu((v) => !v)} aria-label="More" aria-expanded={menu}><MoreVertical size={20} /></button>
          {menu && (
            <div className="cc-menu" role="menu">
              <button
                type="button" role="menuitem" className="cc-menu-item"
                onClick={() => { setMenu(false); if (window.confirm('Switch Claude Code chat off? Open chats will stop streaming.')) toggleEnabled(false); }}
              ><Power size={16} /> Switch off</button>
            </div>
          )}
        </div>
      </header>
      <div className="cc-body">
        {drawer && <div className="cc-backdrop" onClick={() => setDrawer(false)} aria-hidden="true" />}
        <aside className={`cc-side${drawer ? ' cc-side-open' : ''}`}>
          <SessionList sessions={sessions} activeId={activeId} onSelect={(id) => { setActiveId(id); setDrawer(false); }} onNew={newChat} onDelete={deleteChats} onPause={pauseChat} onPlay={playChat} />
        </aside>
        <main className="cc-main">
          {activeId ? <ChatView key={activeId} sessionId={activeId} onDisabled={handleDisabled} /> : (
            <div className="cc-center-fill">
              <div className="cc-card">
                <MessageSquare size={28} className="cc-card-icon" />
                <h2 className="cc-card-title">What should we work on?</h2>
                <button type="button" className="cc-btn cc-btn-primary" onClick={newChat}>Start a new session</button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
